/**
 * Flow scene — shared layout/geometry constants.
 *
 * Pure values pulled out of FlowScene so the scene component, the spark system
 * and any future scene sub-modules share one source of truth (no circular imports
 * back into FlowScene). Numbers only — no THREE objects, no React.
 */

/** Visible tunnel depth in world units (≈ VISIBLE_BEATS_AHEAD beats). */
export const TUNNEL_LENGTH = 140;
/** Total tunnel width across all lanes (world units). */
export const TUNNEL_WIDTH = 4;
/** FIXED per-lane width used for NOTE / keypad SIZING only (world units).
 *  Pinned to TUNNEL_WIDTH / 4 so notes & trigger pills keep the same size
 *  whether the difficulty uses 3, 4 or 5 lanes — only their distribution
 *  changes. (Deliberately NOT FLOW_LANE_COUNT, which is the 5-lane MAX.) */
export const LANE_WIDTH = TUNNEL_WIDTH / 4;

/** Deck width for a lane count. 3–4 lanes keep the base tunnel width (3 lanes
 *  are simply wider); 5 lanes WIDEN the deck instead of squeezing five pills
 *  into four lane-widths, so the centre-to-centre spacing — and the gap
 *  between keypads — keeps the 4-lane feel users asked for. */
export function deckWidthFor(count: number): number {
  return Math.max(TUNNEL_WIDTH, LANE_WIDTH * count);
}

/** Centre-to-centre spacing for `count` lanes spread evenly across the deck.
 *  With 4 (or 5) lanes this equals LANE_WIDTH; with 3 it's wider. */
export function laneSpacing(count: number): number {
  return deckWidthFor(count) / Math.max(1, count);
}

/** X position of a lane centre for a given lane count, lanes spread evenly
 *  across the whole width. Mirrors across X so lane 0 sits on the user's left. */
export function laneCenterXForCount(lane: number, count: number): number {
  return deckWidthFor(count) / 2 - laneSpacing(count) * (lane + 0.5);
}
/** Z of the strike bar (where notes are "hit"). */
export const STRIKE_Z = 0;
/** How many beats of notes are visible ahead of the strike bar. */
export const VISIBLE_BEATS_AHEAD = 16;
/** Note box height / length (world units). */
export const NOTE_HEIGHT = 0.22;
export const NOTE_LENGTH = 0.45;
/** Floor plane Y. */
export const FLOOR_Y = 0;

/**
 * The visible tunnel covers TUNNEL_LENGTH (16 beats ahead), but the runway
 * geometry (floor, lane tints, lane dividers) extends much further so the
 * playhead can keep advancing through a long song without the floor's far
 * edge sliding out from under the upcoming beat labels.
 */
export const RUNWAY_LENGTH = TUNNEL_LENGTH * 20; // ≈ 320 beats of headroom

/** Width threshold (world units): above this value trails switch from thin
 *  Line segments to thick TubeGeometry cylinders (Magic-Tiles / Dancing-Road
 *  style). Value chosen so the default 0.055 renders as a line and anything
 *  the user deliberately nudges upward switches to the full-3D mode. */
export const TUBE_THRESHOLD = 0.10;

/** Edit-window lock (toggled with E): how many beats either side of the
 *  playhead stay editable. Clicks outside the window are ignored, so scrubbing
 *  through a finished section can't wipe notes by accident. */
export const EDIT_WINDOW_BEATS = 8;

/** Play Test: how far off the beat a key press still counts as a hit (ms). */
export const PLAY_TEST_HIT_MS = 120;

/** Snap-division values you cycle through with Ctrl+wheel. */
export const SNAP_OPTIONS = [4, 8, 16, 32, 64, 128, 256, 512];
